import { AlertCircle, Plus, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postApiMedia } from '../../../api/media-client';
import { ImageCompressor } from '../../../util/ImageCompressor';

const MAX_IMAGES = 15;

export interface ImageData {
    id: string; // Unique identifier must be specified
    image: string; // URL string or base64 encoded
    mediaId?: string; // Optional Media Id from the backend.
}

interface UploadBoxProps {
    width: number;
    height: number;
    state: UploadBoxState;
    onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    enableTransitions: boolean;
}

export const UploadBox: React.FC<UploadBoxProps> = ({ width, height, state, onUpload, enableTransitions }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        inputRef.current?.click();
    };

    const boxStyle = useMemo(() => {
        let transitionValue;
        if (enableTransitions) {
            transitionValue = 'left 0.3s ease, top 0.3s ease, transform 0.3s ease, opacity 0.1s ease';
        } else {
            transitionValue = 'opacity 0.2s ease';
        }

        return {
            width: `${width}px`,
            height: `${height}px`,
            left: `${state.x}px`,
            top: `${state.y}px`,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            border: '2px dashed #ccc',
            borderRadius: '8px',
            backgroundColor: '#f8f8f8',
            transition: transitionValue,
        };
    }, [enableTransitions, width, height, state]);


    return (
        <div style={boxStyle} onClick={handleClick} className={`w-full h-full flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition ${false ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}>
            <Plus className="text-gray-400" size={40} />
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onUpload}
                style={{ display: 'none' }}
            />
        </div>
    );
};

interface ImageBoxState {
    imageData: ImageData;
    x: number;
    y: number;
    zIndex: number;
    visualIndex: number;
    isUploading: boolean;
    isError: boolean;
    renderId: number;
}

interface UploadBoxState {
    x: number;
    y: number;
}

interface BoxStates {
    imageBoxStates: ImageBoxState[];
    uploadBoxState: UploadBoxState;
}

interface ImageItemProps {
    width: number;
    height: number;
    boxId: string;
    imageBoxState: ImageBoxState;
    imageData: ImageData;
    onDragStart: (boxId: string, clientX: number, clientY: number) => void;
    onDelete: (boxId: string) => void;
    isDragging: boolean;
    enableTransitions: boolean;
}

export const ImageItem: React.FC<ImageItemProps> = ({
    width,
    height,
    boxId,
    imageBoxState,
    imageData,
    onDragStart,
    onDelete,
    isDragging,
    enableTransitions,
}) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [isHolding, setIsHolding] = useState(false);
    const holdTimerRef = useRef<number | null>(null);
    const isTouch = useRef(false);

    // Literally a hack to try and cover up that the images move on second render
    // Fade it in so you don't see the first frame
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        setIsVisible(true);
    }, []);

    const boxStyle = useMemo(() => {
        let transitionValue;
        if (enableTransitions) {
            transitionValue = isDragging
                ? 'transform 0.3s ease, opacity 0.1s ease'
                : 'left 0.3s ease, top 0.3s ease, transform 0.3s ease, opacity 0.1s ease';
        } else {
            transitionValue = 'opacity 0.2s ease';
        }

        return {
            width: `${width}px`,
            height: `${height}px`,
            position: 'absolute' as const,
            left: `${imageBoxState.x}px`,
            top: `${imageBoxState.y}px`,
            cursor: 'pointer',
            userSelect: 'none' as const,
            zIndex: imageBoxState.zIndex,
            transition: transitionValue,
            opacity: isVisible ? 1 : 0,
            overflow: 'hidden',
            transform: isDragging && isTouch.current ? 'scale(1.10)' : isHolding ? 'scale(0.95)' : 'scale(1)',
        };
    }, [imageBoxState, isDragging, enableTransitions, isHolding, isVisible, width, height]);

    const imageStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        filter: imageBoxState.isUploading
            ? 'brightness(0.7)'
            : isDragging && isTouch.current
                ? 'brightness(1.1)'
                : 'none',
        WebkitTouchCallout: 'none',
        pointerEvents: 'none',
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        isTouch.current = false;
        onDragStart(boxId, e.clientX, e.clientY);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault();
        isTouch.current = true;
        setIsHolding(true);
        holdTimerRef.current = window.setTimeout(() => {
            onDragStart(boxId, e.touches[0].clientX, e.touches[0].clientY);
            setIsHolding(false);
        }, 300);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
            setIsHolding(false);
        }
    };

    const handleTouchEnd = () => {
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        setIsHolding(false);
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDelete(boxId);
    };

    // Prevent the context menu from showing when tapping and holding on chrome touch
    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
    };

    useEffect(() => {
        return () => {
            if (holdTimerRef.current) {
                clearTimeout(holdTimerRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={itemRef}
            style={boxStyle}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onContextMenu={handleContextMenu}
        >
            <img src={imageData.image} alt={`Image ${boxId}`} style={imageStyle} />

            <button
                onClick={handleDelete}
                className="absolute select-none top-2 right-2 p-1 bg-gray-800 text-white rounded-full opacity-50 group-hover:opacity-100 transition-opacity z-10"
            >
                <X size={16} />
            </button>

            {imageBoxState.isUploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin select-none inline-block size-10 border-[4px] border-current border-t-transparent text-[#2476f2] rounded-full" role="status" aria-label="loading">
                        <span className="sr-only">Uploading...</span>
                    </div>
                </div>
            )}

            {imageBoxState.isError && (
                <div className="absolute inset-0 select-none flex items-center justify-center bg-black bg-opacity-50">
                    <div className="text-white select-none text-center">
                        <AlertCircle size={24} className="mx-auto mb-2" />
                        <p className="text-sm">Upload failed</p>
                    </div>
                </div>
            )}
        </div>
    );
};

function isEqual(arr1: ImageData[], arr2: ImageData[]): boolean {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i].id !== arr2[i].id) return false;
    }
    return true;
}

interface UseImageArrangerProps {
    initialImages: ImageData[];
    onImagesUpdate?: (images: ImageData[]) => void;
}

export const useImageArranger = ({
    initialImages,
    onImagesUpdate
}: UseImageArrangerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const previousImagesRef = useRef<ImageData[]>([]);

    const [draggingId, setDraggingId] = useState<string | null>(null);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const renderOrderRef = useRef(0);
    const lastTouchRef = useRef({ x: 0, y: 0, time: 0 });

    const [width, setWidth] = useState(0);
    const [isMounted, setIsMounted] = useState(false);
    const [enableTransitions, setEnableTransitions] = useState(false);

    const [shouldReset, setShouldReset] = useState(false);

    const resetArrangement = useCallback(() => {
        console.log("set reset arrangement");
        setShouldReset(true);
    }, []);

    // Effect to measure width when the component is mounted
    useEffect(() => {
        if (isMounted && containerRef.current) {
            console.log("setting width ", containerRef.current.offsetWidth);
            setWidth(containerRef.current.offsetWidth);
            const timer = setTimeout(() => {
                setEnableTransitions(true);
                console.log("Enabling transitions");
            }, 100); // Increased timeout to ensure it happens after layout
            return () => clearTimeout(timer);
        } else {
            setEnableTransitions(false);
        }
    }, [isMounted]);

    const [boxStates, setBoxStates] = useState<BoxStates>({
        imageBoxStates: [],
        uploadBoxState: { x: 0, y: 0 }
    });

    // Effect to watch boxStates.imageBoxStates and send updates to onImagesUpdate if there's a diff
    useEffect(() => {
        const newImages = boxStates.imageBoxStates
            .filter(state => !state.isUploading && !state.isError)
            .map(state => state.imageData);

        if (!isEqual(previousImagesRef.current, newImages)) {
            previousImagesRef.current = newImages;
            if (onImagesUpdate) {
                onImagesUpdate(newImages);
            }
        }
    }, [boxStates.imageBoxStates, onImagesUpdate]);

    const updateBoxStates = useCallback((updater: (prevStates: BoxStates) => BoxStates) => {
        setBoxStates(prevStates => {
            const newStates = updater(prevStates);
            return newStates;
        });
    }, []);

    const calculateGridPositions = useMemo(() => (width: number, boxes: ImageBoxState[]): BoxStates => {
        if (width < 10.0) {
            console.log("width is less than 10");
            return {
                imageBoxStates: [],
                uploadBoxState: { x: -500, y: -500 }
            };
        }

        console.log("calculate grid positions, ", width);

        const columns = getColumnCount(width);
        const padding = 20;
        const boxWidth = (width - (columns + 1) * padding) / columns;

        const sortedBoxes = [...boxes].sort((a, b) => a.visualIndex - b.visualIndex);
        const totalItems = sortedBoxes.length + 1; // +1 for the upload box
        const totalRows = Math.ceil(totalItems / columns);

        const itemsInLastRow = totalItems % columns || columns;

        const updatedImageBoxStates = sortedBoxes.map((pos, index) => {
            const row = Math.floor(index / columns);
            const col = index % columns;

            let x;
            if (row === totalRows - 1 && itemsInLastRow < columns) {
                const lastRowWidth = itemsInLastRow * boxWidth + (itemsInLastRow - 1) * padding;
                const offset = (width - lastRowWidth) / 2;
                x = offset + col * (boxWidth + padding);
            } else {
                x = padding + col * (boxWidth + padding);
            }

            const y = padding + row * (boxWidth + padding);
            return { ...pos, x, y };
        });

        const uploadBoxIndex = totalItems - 1;
        const uploadBoxRow = Math.floor(uploadBoxIndex / columns);
        const uploadBoxCol = uploadBoxIndex % columns;

        let uploadBoxX;
        if (uploadBoxRow === totalRows - 1 && itemsInLastRow < columns) {
            const lastRowWidth = itemsInLastRow * boxWidth + (itemsInLastRow - 1) * padding;
            const offset = (width - lastRowWidth) / 2;
            uploadBoxX = offset + uploadBoxCol * (boxWidth + padding);
        } else {
            uploadBoxX = padding + uploadBoxCol * (boxWidth + padding);
        }

        const uploadBoxY = padding + uploadBoxRow * (boxWidth + padding);

        return {
            imageBoxStates: updatedImageBoxStates,
            uploadBoxState: { x: uploadBoxX, y: uploadBoxY }
        };
    }, []);

    const updatePositions = useCallback((draggedId: string | null = null, dragX: number | null = null, dragY: number | null = null) => {
        if (!containerRef.current) return;
        console.log("update positions");

        const width = containerRef.current.offsetWidth;
        setBoxStates(prevStates => {
            const newStates = calculateGridPositions(width, prevStates.imageBoxStates);
            return {
                ...newStates,
                imageBoxStates: newStates.imageBoxStates.map(state => ({
                    ...state,
                    zIndex: state.imageData.id === draggedId ? 1000 : 1,
                    x: state.imageData.id === draggedId && dragX !== null ? dragX : state.x,
                    y: state.imageData.id === draggedId && dragY !== null ? dragY : state.y,
                }))
            };
        });

    }, [calculateGridPositions]);

    const handleDragStart = useCallback((boxId: string, clientX: number, clientY: number) => {
        if (!containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const draggedBox = boxStates.imageBoxStates.find(state => state.imageData.id === boxId);
        if (!draggedBox) return;

        dragStartRef.current = {
            x: clientX - containerRect.left - draggedBox.x,
            y: clientY - containerRect.top - draggedBox.y
        };
        setDraggingId(boxId);

        updatePositions(boxId, draggedBox.x, draggedBox.y);
    }, [boxStates, updatePositions]);


    const handleDrag = useCallback((clientX: number, clientY: number, isTouch: boolean) => {
        if (draggingId === null || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRef.current.offsetWidth;
        console.log("handle drag width ", containerWidth);
        const columns = getColumnCount(containerWidth);
        const padding = 20;
        const boxWidth = (containerWidth - (columns + 1) * padding) / columns;

        const newX = clientX - containerRect.left - dragStartRef.current.x;
        const newY = clientY - containerRect.top - dragStartRef.current.y;

        updateBoxStates(prevStates => {
            console.log("update box states");
            const draggedBox = prevStates.imageBoxStates.find(pos => pos.imageData.id === draggingId);
            if (!draggedBox) return prevStates;

            const draggedRect = {
                left: newX,
                top: newY,
                right: newX + boxWidth,
                bottom: newY + boxWidth,
            };

            let maxOverlap = 0;
            let targetIndex = draggedBox.visualIndex;

            prevStates.imageBoxStates.forEach((box, index) => {
                if (box.imageData.id === draggingId) return;

                const boxRect = {
                    left: box.x,
                    top: box.y,
                    right: box.x + boxWidth,
                    bottom: box.y + boxWidth,
                };

                const xOverlap = Math.max(0, Math.min(draggedRect.right, boxRect.right) - Math.max(draggedRect.left, boxRect.left));
                const yOverlap = Math.max(0, Math.min(draggedRect.bottom, boxRect.bottom) - Math.max(draggedRect.top, boxRect.top));
                const overlapArea = xOverlap * yOverlap;

                if (overlapArea > maxOverlap && overlapArea > (boxWidth * boxWidth) / 2) {
                    maxOverlap = overlapArea;
                    targetIndex = box.visualIndex;
                }
            });

            const newImageBoxStates = prevStates.imageBoxStates.map(pos => {
                if (pos.imageData.id === draggingId) {
                    return { ...pos, x: newX, y: newY, zIndex: 1000, visualIndex: targetIndex };
                } else if (pos.visualIndex >= Math.min(targetIndex, draggedBox.visualIndex) && pos.visualIndex <= Math.max(targetIndex, draggedBox.visualIndex)) {
                    const direction = targetIndex > draggedBox.visualIndex ? -1 : 1;
                    return { ...pos, visualIndex: pos.visualIndex + direction };
                }
                return pos;
            });

            const newBoxStates = calculateGridPositions(containerWidth, newImageBoxStates);
            return {
                ...newBoxStates,
                imageBoxStates: newBoxStates.imageBoxStates.map(pos =>
                    pos.imageData.id === draggingId
                        ? { ...pos, x: newX, y: newY, zIndex: 1000 }
                        : { ...pos, zIndex: 1 }
                )
            };
        });
    }, [draggingId, calculateGridPositions]);

    const handleDragEnd = useCallback(() => {
        if (draggingId !== null) {
            updatePositions();
        }
        setDraggingId(null);
    }, [draggingId, updatePositions]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (draggingId !== null) {
            handleDrag(e.clientX, e.clientY, false);
        }
    }, [draggingId, handleDrag]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (draggingId !== null) {
            e.preventDefault();
            const touch = e.touches[0];
            handleDrag(touch.clientX, touch.clientY, true);
        } else {
            const touch = e.touches[0];
            const now = Date.now();
            const deltaX = touch.clientX - lastTouchRef.current.x;
            const deltaY = touch.clientY - lastTouchRef.current.y;
            const deltaTime = now - lastTouchRef.current.time;

            if (deltaTime > 0) {
                const velocity = Math.sqrt(deltaX * deltaX + deltaY * deltaY) / deltaTime;
                if (velocity > 0.5) {
                    // Cancel any ongoing hold operations if scrolling is detected
                    const holdingItem = document.querySelector('.holding');
                    if (holdingItem) {
                        holdingItem.classList.remove('holding');
                    }
                }
            }

            lastTouchRef.current = { x: touch.clientX, y: touch.clientY, time: now };
        }
    }, [draggingId, handleDrag]);

    const handleMouseUp = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    const handleTouchEnd = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    const handleDelete = useCallback((boxIdToDelete: string) => {
        if (!containerRef.current) return;

        if (boxStates.imageBoxStates.length <= 1) {
            alert(`You need at least 1 image.`);
            return;
        }

        const containerWidth = containerRef.current.offsetWidth;
        updateBoxStates(prevStates => {
            console.log("update box states 1");
            const deletedBoxVisualIndex = prevStates.imageBoxStates.find(state => state.imageData.id === boxIdToDelete)?.visualIndex;
            if (deletedBoxVisualIndex === undefined) return prevStates;

            const newImageBoxStates = prevStates.imageBoxStates
                .filter(state => state.imageData.id !== boxIdToDelete)
                .map(state => state.visualIndex > deletedBoxVisualIndex ? { ...state, visualIndex: state.visualIndex - 1 } : state);

            return calculateGridPositions(containerWidth, newImageBoxStates);
        });
    }, [calculateGridPositions, boxStates.imageBoxStates]);

    const onImageUpload = async (file: File): Promise<string> => {
        try {
            // Process image to create small and large variants
            const { smallVersion, largeVersion } = await ImageCompressor.processImage(file);
    
            // Create form data with both variants
            const formData = {
                smallVariant: new Blob([smallVersion.file], { type: 'image/webp' }),
                largeVariant: new Blob([largeVersion.file], { type: 'image/webp' })
            };
    
            // Upload to server
            const response = await postApiMedia({ formData });
    
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || 'Upload failed');
            }
    
            return response.data.id;
        } catch (error) {
            throw error;
        }
    };

    const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!containerRef.current) return;
        const containerWidth = containerRef.current.offsetWidth;
    
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
    
        // Check total number of images after adding new ones
        if (boxStates.imageBoxStates.length + files.length > MAX_IMAGES) {
            alert(`You can only upload a maximum of ${MAX_IMAGES} images.`);
            return;
        }
    
        // Process each file
        const uploadPromises = files.map(async (file) => {
            return new Promise<ImageBoxState>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    const tempId = `temp-${Date.now()}-${Math.random()}`;
                    const newImageBoxState: ImageBoxState = {
                        imageData: { id: tempId, image: base64 },
                        x: 0,
                        y: 0,
                        zIndex: 1,
                        visualIndex: boxStates.imageBoxStates.length,
                        isUploading: true,
                        isError: false,
                        renderId: renderOrderRef.current,
                    };
                    renderOrderRef.current += 1;
                    resolve(newImageBoxState);
                };
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(file);
            });
        });
    
        try {
            // Create and add all image box states first
            const newImageBoxStates = await Promise.all(uploadPromises);
            
            updateBoxStates(prevStates => {
                const updatedImageBoxStates = [...prevStates.imageBoxStates, ...newImageBoxStates];
                return calculateGridPositions(containerWidth, updatedImageBoxStates);
            });
    
            // Then start uploading each image to the server
            newImageBoxStates.forEach(async (imageBoxState, index) => {
                try {
                    const file = files[index];
                    const newMediaId = await onImageUpload(file);
                    
                    updateBoxStates(prevStates => {
                        const updatedImageBoxStates = prevStates.imageBoxStates.map(state =>
                            state.imageData.id === imageBoxState.imageData.id 
                                ? { ...state, imageData: { ...state.imageData, mediaId: newMediaId }, isUploading: false }
                                : state
                        );
                        return calculateGridPositions(containerWidth, updatedImageBoxStates);
                    });
                } catch (error) {
                    updateBoxStates(prevStates => ({
                        ...prevStates,
                        imageBoxStates: prevStates.imageBoxStates.map(state =>
                            state.imageData.id === imageBoxState.imageData.id
                                ? { ...state, isUploading: false, isError: true }
                                : state
                        )
                    }));
                }
            });
        } catch (error) {
            console.error('Error processing files:', error);
        }
    
        // Reset the file input to allow the same files to be uploaded again
        event.target.value = '';
    }, [calculateGridPositions, onImageUpload, boxStates.imageBoxStates.length]);

    const [isDraggingOver, setIsDraggingOver] = useState(false);

    // Handle drag events
    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if files are being dragged
        if (e.dataTransfer.types.includes('Files')) {
            setIsDraggingOver(true);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Only set dragging over to false if we're leaving the container (not entering a child element)
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
            setIsDraggingOver(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('image/')
        );

        if (droppedFiles.length === 0) return;

        // Create a synthetic event that matches what handleImageUpload expects
        const syntheticEvent = {
            target: {
                files: droppedFiles,
                value: ''
            }
        } as React.ChangeEvent<HTMLInputElement>;

        handleImageUpload(syntheticEvent);
    }, [handleImageUpload]);

    const containerStyle = useCallback(() => {
        if (!containerRef.current) return {
            position: 'relative' as const,
            width: '100%',
            height: '100px',
        };

        const containerWidth = containerRef.current.offsetWidth;
        const columns = getColumnCount(containerWidth);
        const padding = 20;
        const boxWidth = (containerWidth - (columns + 1) * padding) / columns;

        return {
            position: 'relative' as const,
            width: '100%',
            height: `${Math.ceil((boxStates.imageBoxStates.length + 1) / columns) * (boxWidth + padding) + padding}px`,
            transition: enableTransitions ? 'height 0.3s ease' : 'none',
            backgroundColor: isDraggingOver ? 'rgba(59, 130, 246, 0.1)' : 'transparent', // Light blue background when dragging
            outline: isDraggingOver ? '2px dashed rgb(59, 130, 246)' : 'none', // Blue dashed outline when dragging
        };
    }, [boxStates.imageBoxStates.length, enableTransitions, isDraggingOver]);

    const handleResize = useCallback(() => {
        updatePositions();
    }, [updatePositions]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    useEffect(() => {
        console.log("effect triggered. initial images: ", initialImages);
        const initialStates: ImageBoxState[] = initialImages.map((img, index) => ({
            imageData: img,
            x: 0,
            y: 0,
            zIndex: 1,
            visualIndex: index,
            isUploading: false,
            isError: false,
            renderId: index,
        }));
        updateBoxStates(() => calculateGridPositions(width, initialStates));

        renderOrderRef.current = initialStates.length;

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [width, calculateGridPositions, handleResize]);

    useEffect(() => {
        if (shouldReset) {
            const initialStates: ImageBoxState[] = initialImages.map((img, index) => ({
                imageData: img,
                x: 0,
                y: 0,
                zIndex: 1,
                visualIndex: index,
                isUploading: false,
                isError: false,
                renderId: index,
            }));
            console.log("update box states 5");
            updateBoxStates(() => calculateGridPositions(width, initialStates));
            renderOrderRef.current = initialStates.length;
            setShouldReset(false);
        }

    }, [width, initialImages, calculateGridPositions, shouldReset]);

    return {
        containerRef,
        draggingId,
        boxStates,
        handleImageUpload,
        handleDragStart,
        handleDelete,
        containerStyle,
        setIsMounted,
        enableTransitions,
        resetArrangement,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        isDraggingOver,
    };
};

const getColumnCount = (containerWidth: number): number => {
    if (containerWidth < 500) return 2;
    if (containerWidth < 700) return 3;
    return 4;
};
